import { useMemo, useState } from 'react';
import { Webhook, ExternalLink, AlertTriangle, PlayCircle, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { MarketingPageShell } from './components/MarketingPageShell';
import { StatusBadge } from './components/marketing-ui';
import {
  useProvidersQuery,
  useListenProvidersQuery,
  useRunConformanceMutation,
} from '@/client/api/marketing/sources';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  CAPABILITY_SUPPORT_BADGE,
  CAPABILITY_SUPPORT_LABEL,
  PROVIDERS,
  PROVIDER_CAPABILITY_LABEL,
  READ_BACK_CAPABILITIES,
  readBackScore,
  type ProviderCapability,
  type ProviderRecord,
} from '@/client/types/marketing/providers';
import type { ApiListenProvider, ApiProvider, ListenCapability } from '@/client/types/marketing/api';

const ALL_CAPABILITIES = Object.keys(PROVIDER_CAPABILITY_LABEL) as ProviderCapability[];

const LISTEN_CAPABILITY_LABEL: Record<ListenCapability, string> = {
  author_feed: 'Author feed (from:@)',
  hashtag_feed: 'Hashtag feed',
  search_keyword: 'Keyword search',
  search_mention: 'Mention search',
};

const LISTEN_CAPABILITIES = Object.keys(LISTEN_CAPABILITY_LABEL) as ListenCapability[];

function listenCapabilitySupport(
  provider: ApiListenProvider,
  cap: ListenCapability,
): { label: string; className: string; note?: string } {
  const platforms = provider.capabilities[cap];
  if (!platforms?.length) {
    return { label: 'No', className: CAPABILITY_SUPPORT_BADGE.none };
  }
  if (platforms.includes('*')) {
    return { label: 'Full', className: CAPABILITY_SUPPORT_BADGE.full };
  }
  return {
    label: 'Partial',
    className: CAPABILITY_SUPPORT_BADGE.partial,
    note: platforms.join(', '),
  };
}

/**
 * Merge the live capability matrix over the researched one.
 *
 * The two carry different things and neither subsumes the other:
 *
 *  - The **API** is authoritative on what the adapters actually implement, plus
 *    the runtime facts only the server knows — is the provider enabled, does this
 *    deployment hold a key, which is the default, when did conformance last run.
 *  - The **static** file carries the research: the per-capability notes, and the
 *    `unknown` marker for capabilities a vendor never documented. The server has
 *    no way to express "undocumented" — its model omits undeclared capabilities
 *    entirely, so absent means both "no" and "unknown" to it.
 *
 * Collapsing to the API alone would turn every undocumented capability into a
 * flat "No", which is the same false-confidence error as reporting a handle dead
 * because the check timed out. So the API narrows support and supplies runtime
 * state; the static layer keeps the notes and the honest ambiguity.
 */
function mergeProviders(live: ApiProvider[] | undefined): ProviderRecord[] {
  if (!live?.length) return PROVIDERS;
  const byId = new Map(live.map((p) => [p.provider, p]));

  return PROVIDERS.map((base) => {
    const remote = byId.get(base.id);
    if (!remote) return base;

    const capabilities = { ...base.capabilities };
    for (const cap of ALL_CAPABILITIES) {
      const platforms = remote.capabilities[cap];
      const current = base.capabilities[cap];
      if (!platforms) {
        // Not declared by the adapter. Keep a documented `unknown` as unknown;
        // otherwise it really is unsupported.
        if (current?.support !== 'unknown') {
          capabilities[cap] = { support: 'none', note: current?.note };
        }
        continue;
      }
      capabilities[cap] = {
        support: platforms.includes('*') ? 'full' : 'partial',
        note: current?.note,
      };
    }

    return {
      ...base,
      capabilities,
      platforms: remote.platforms?.length ? remote.platforms : base.platforms,
      enabled: remote.enabled,
      isDefault: remote.is_default,
    };
  });
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

export default function ProvidersPage() {
  const [readBackOnly, setReadBackOnly] = useState(false);
  const { data, isLoading, isError } = useProvidersQuery();
  const listenQ = useListenProvidersQuery();
  const conformance = useRunConformanceMutation();

  const live = data?.providers;
  const providers = useMemo(() => mergeProviders(live), [live]);
  const liveById = useMemo(
    () => new Map((live ?? []).map((p) => [p.provider, p])),
    [live],
  );
  const listenProviders = listenQ.data?.providers ?? [];

  const rows = readBackOnly ? READ_BACK_CAPABILITIES : ALL_CAPABILITIES;
  const enabled = providers.filter((p) => p.enabled);
  const unknownCount = providers.filter((p) => p.appRegistrations === 'unknown').length;

  const runConformance = (provider: ProviderRecord) => {
    conformance.mutate(
      { provider: provider.id },
      {
        onSuccess: () =>
          toast.success(`Conformance run complete for ${provider.name}`, {
            description: 'Read-only probes only — nothing was published.',
          }),
        onError: (error) =>
          toast.error(getApiErrorMessage(error, `Could not run conformance for ${provider.name}`)),
      },
    );
  };

  return (
    <MarketingPageShell
      title="Providers"
      description="The publishing rail. We buy this rather than build five OAuth integrations, because the cost is app review, not code. All providers sit behind one contract, so switching is a swap."
      crumbs={[{ label: 'Marketing' }, { label: 'Sources' }, { label: 'Providers' }]}
      icon={Webhook}
    >
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Providers wired" value={String(providers.length)} hint={`${enabled.length} enabled`} />
        <StatCard
          label="Default"
          value={providers.find((p) => p.isDefault)?.name ?? '—'}
          hint="used for new connections"
        />
        <StatCard label="Read-back leaders" value={`${Math.max(...providers.map(readBackScore))} / ${READ_BACK_CAPABILITIES.length}`} hint="capabilities Radar depends on" />
        <StatCard label="Unconfirmed app model" value={String(unknownCount)} hint="blocks go-live, not build" />
      </div>

      {unknownCount > 0 && (
        <div className="mb-6 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold">Whose platform app registrations?</div>
            <p className="mt-1 text-amber-800">
              {unknownCount} of {providers.length} providers do not document this. It is the single question that
              decides whether buying actually removes the Meta App Review / LinkedIn CMA / paid-X bottleneck — if a
              provider requires our own app registrations, it saves code but not approvals. Confirm per platform,
              TikTok especially, before go-live.
            </p>
          </div>
        </div>
      )}

      <div className="mb-3 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={readBackOnly}
            onChange={(e) => setReadBackOnly(e.target.checked)}
            className="rounded border-gray-300"
          />
          Show only read-back capabilities
        </label>
        {/* Say which matrix is on screen. A capability table whose provenance is
            unclear is worse than none — someone will make a vendor decision on it. */}
        {isLoading ? (
          <span className="text-xs text-gray-400">Loading live capabilities…</span>
        ) : live?.length ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700">
            <Wifi className="h-3.5 w-3.5" />
            Live from ff-api-external, annotated with vendor docs
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1.5 text-xs text-amber-700"
            title={isError ? 'The providers endpoint could not be reached' : undefined}
          >
            <WifiOff className="h-3.5 w-3.5" />
            Vendor docs only — live capabilities unavailable
          </span>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500">
                Capability
              </th>
              {providers.map((p) => {
                const remote = liveById.get(p.id);
                return (
                  <th key={p.id} className="px-4 py-3 text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{p.name}</span>
                      {p.isDefault && (
                        <span className="rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                          default
                        </span>
                      )}
                      {!p.enabled && (
                        <span className="rounded-full border border-gray-200 bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                          off
                        </span>
                      )}
                      {/* A provider can be enabled and still hold no key in this
                          environment — worth distinguishing, since it looks
                          identical until the first call fails. */}
                      {remote && !remote.configured && (
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">
                          no key
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-[11px] font-normal text-gray-500">{p.costNote.split('.')[0]}</div>
                    <div className="mt-1 text-[11px] font-normal text-gray-400">
                      read-back {readBackScore(p)}/{READ_BACK_CAPABILITIES.length}
                    </div>
                    {remote?.last_conformance && (
                      <div className="mt-1 text-[11px] font-normal text-gray-400">
                        last run: {remote.last_conformance.passed}✓ {remote.last_conformance.failed}✗{' '}
                        {remote.last_conformance.unsupported}–
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((cap) => (
              <tr key={cap} className="hover:bg-gray-50">
                <td className="sticky left-0 z-10 bg-white px-4 py-3 font-medium text-gray-800">
                  {PROVIDER_CAPABILITY_LABEL[cap]}
                </td>
                {providers.map((p) => {
                  const fact = p.capabilities[cap];
                  return (
                    <td key={p.id} className="px-4 py-3 align-top">
                      <StatusBadge
                        label={CAPABILITY_SUPPORT_LABEL[fact.support]}
                        className={CAPABILITY_SUPPORT_BADGE[fact.support]}
                      />
                      {fact.note && <div className="mt-1 max-w-[16rem] text-[11px] text-gray-500">{fact.note}</div>}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="bg-gray-50/60">
              <td className="sticky left-0 z-10 bg-gray-50/60 px-4 py-3 font-medium text-gray-800">
                Own app registrations
              </td>
              {providers.map((p) => (
                <td key={p.id} className="px-4 py-3 align-top">
                  <StatusBadge
                    label={CAPABILITY_SUPPORT_LABEL[p.appRegistrations]}
                    className={CAPABILITY_SUPPORT_BADGE[p.appRegistrations]}
                  />
                  <div className="mt-1 max-w-[16rem] text-[11px] text-gray-500">{p.appRegistrationsNote}</div>
                </td>
              ))}
            </tr>
            <tr>
              <td className="sticky left-0 z-10 bg-white px-4 py-3 font-medium text-gray-800">Actions</td>
              {providers.map((p) => (
                <td key={p.id} className="px-4 py-3">
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => runConformance(p)}
                      disabled={conformance.isPending}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <PlayCircle className="h-3.5 w-3.5" />
                      {conformance.isPending ? 'Running…' : 'Run conformance'}
                    </button>
                    <a
                      href={p.docsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-blue-700 hover:underline"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> API docs
                    </a>
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <p className="mt-4 max-w-3xl text-xs text-gray-500">
        <strong>Undocumented is not unsupported.</strong> Where a vendor simply does not publish a capability we show
        it as undocumented rather than guessing — the same reason handle checks return <em>indeterminate</em> rather
        than <em>not found</em> when the check itself fails. A conformance run resolves those into real answers by
        exercising a live connection.
      </p>

      <div className="mt-10 mb-3">
        <h2 className="text-base font-semibold text-gray-900">Listen providers</h2>
        <p className="mt-1 max-w-3xl text-sm text-gray-500">
          Public scrape / discover rail (Apify, SERP). Separate from publishing — different vendors, keys, and
          failure modes. Radar Tracked Performance and listening Sync depend on this matrix, not Zernio.
        </p>
      </div>

      {listenQ.isLoading ? (
        <p className="text-sm text-gray-400">Loading listen providers…</p>
      ) : listenQ.isError ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Listen providers endpoint unavailable — discovery may still work if the server has a default provider.
        </div>
      ) : listenProviders.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">
          No listen providers configured in this environment.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-500">
                  Capability
                </th>
                {listenProviders.map((p) => (
                  <th key={p.provider} className="px-4 py-3 text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold capitalize text-gray-900">{p.provider}</span>
                      {!p.enabled && (
                        <span className="rounded-full border border-gray-200 bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                          off
                        </span>
                      )}
                      {!p.configured && (
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">
                          no key
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-[11px] font-normal text-gray-400">
                      {(p.platforms ?? []).join(', ') || 'platforms TBD'}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {LISTEN_CAPABILITIES.map((cap) => (
                <tr key={cap} className="hover:bg-gray-50">
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 font-medium text-gray-800">
                    {LISTEN_CAPABILITY_LABEL[cap]}
                  </td>
                  {listenProviders.map((p) => {
                    const fact = listenCapabilitySupport(p, cap);
                    return (
                      <td key={p.provider} className="px-4 py-3 align-top">
                        <StatusBadge label={fact.label} className={fact.className} />
                        {fact.note && (
                          <div className="mt-1 max-w-[16rem] text-[11px] text-gray-500">{fact.note}</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </MarketingPageShell>
  );
}
